import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Zap,
  Star,
  TrendingUp,
  Heart,
  Crown,
  Sparkles,
  CheckCircle,
  DollarSign,
  Lightbulb,
  Copy,
  MessageCircle,
  Eye
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fetchAdminPremiumPlans } from '../../store/slices/adminSlice';
import { useTheme, themes } from '../ThemeProvider';


// Add example plans templates
const examplePlans = [
  // ADDON PLANS - Buy Extra Contact Views (requires active plan)
  {
    name: 'Extra Contact Views - 10 Pack',
    description: 'Add 10 extra contact views to your active subscription',
    credits: 0,
    creditsValue: 10,
    price: 99,
    features: [
      '10 additional contact views',
      'Instantly added to your active plan',
      'Never expires while plan is active',
      'Perfect for when you run out'
    ],
    icon: 'Eye',
    color: 'from-blue-500 to-cyan-500',
    badge: 'ADDON',
    category: 'Features',
    addonType: 'contact_views',
    requiresActivePlan: true
  },
  {
    name: 'Extra Contact Views - 50 Pack',
    description: 'Add 50 extra contact views to your active subscription',
    credits: 0,
    creditsValue: 50,
    price: 399,
    features: [
      '50 additional contact views',
      'Instantly added to your active plan',
      'Never expires while plan is active',
      'Best value for money'
    ],
    icon: 'Eye',
    color: 'from-blue-500 to-cyan-500',
    badge: 'POPULAR',
    category: 'Features',
    addonType: 'contact_views',
    requiresActivePlan: true
  },
  {
    name: 'Extra Contact Views - 100 Pack',
    description: 'Add 100 extra contact views to your active subscription',
    credits: 0,
    creditsValue: 100,
    price: 699,
    features: [
      '100 additional contact views',
      'Instantly added to your active plan',
      'Never expires while plan is active',
      'Maximum value pack'
    ],
    icon: 'Eye',
    color: 'from-blue-500 to-cyan-500',
    badge: 'BEST VALUE',
    category: 'Features',
    addonType: 'contact_views',
    requiresActivePlan: true
  },

  // ADDON PLANS - Buy Super Interests (requires active plan)
  {
    name: 'Super Interests - 10 Pack',
    description: 'Add 10 super interests to your active subscription',
    credits: 0,
    creditsValue: 10,
    price: 149,
    features: [
      '10 super interest credits',
      'Stand out from regular interests',
      '5x higher response rate',
      'Added to your active plan'
    ],
    icon: 'Star',
    color: 'from-amber-500 to-orange-500',
    badge: 'ADDON',
    category: 'Engagement',
    addonType: 'super_interests',
    requiresActivePlan: true
  },
  {
    name: 'Super Interests - 30 Pack',
    description: 'Add 30 super interests to your active subscription',
    credits: 0,
    creditsValue: 30,
    price: 399,
    features: [
      '30 super interest credits',
      'Stand out from regular interests',
      '5x higher response rate',
      'Best value for active users'
    ],
    icon: 'Star',
    color: 'from-amber-500 to-orange-500',
    badge: 'POPULAR',
    category: 'Engagement',
    addonType: 'super_interests',
    requiresActivePlan: true
  },

  // ADDON PLANS - Buy Regular Interests (requires active plan)
  {
    name: 'Interest Credits - 20 Pack',
    description: 'Add 20 regular interest credits to your active subscription',
    credits: 0,
    creditsValue: 20,
    price: 99,
    features: [
      '20 regular interest credits',
      'Send more connection requests',
      'Increase your match rate',
      'Added to your active plan'
    ],
    icon: 'Heart',
    color: 'from-pink-500 to-rose-500',
    badge: 'ADDON',
    category: 'Engagement',
    addonType: 'interests',
    requiresActivePlan: true
  },
  {
    name: 'Interest Credits - 50 Pack',
    description: 'Add 50 regular interest credits to your active subscription',
    credits: 0,
    creditsValue: 50,
    price: 199,
    features: [
      '50 regular interest credits',
      'Send more connection requests',
      'Increase your match rate',
      'Great value pack'
    ],
    icon: 'Heart',
    color: 'from-pink-500 to-rose-500',
    badge: 'POPULAR',
    category: 'Engagement',
    addonType: 'interests',
    requiresActivePlan: true
  },

  // ADDON PLANS - Video Call Credits (requires active plan)
  {
    name: 'Video Call Credits - 10 Pack',
    description: 'Add 10 video call credits to your active subscription',
    credits: 0,
    creditsValue: 10,
    price: 199,
    features: [
      '10 video call credits',
      'Connect face-to-face with matches',
      'Build trust before meeting',
      'Added to your active plan'
    ],
    icon: 'MessageCircle',
    color: 'from-green-500 to-emerald-500',
    badge: 'ADDON',
    category: 'Communication',
    addonType: 'video_calls',
    requiresActivePlan: true
  },

  // PROFILE BOOSTER PLANS - Independent (no active plan required)
  {
    name: 'Profile Booster - Basic',
    description: 'Get 10x more visibility and appear first in search results',
    credits: 50,
    creditsValue: 50,
    price: 299,
    features: [
      '50 sponsored impressions',
      'Appear in top 3 search results',
      'Show up first to potential matches',
      'Basic analytics dashboard',
      'No subscription required'
    ],
    icon: 'TrendingUp',
    color: 'from-blue-500 to-cyan-500',
    badge: '',
    category: 'Profile Booster',
    addonType: 'profile_booster',
    requiresActivePlan: false
  },
  {
    name: 'Profile Booster - Popular',
    description: 'Most popular plan with maximum visibility',
    credits: 100,
    creditsValue: 100,
    price: 499,
    features: [
      '100 sponsored impressions',
      'Top search results priority',
      'Featured profile badge',
      'Advanced analytics with insights',
      'No subscription required',
      '1 credit = 1 profile click'
    ],
    icon: 'Zap',
    color: 'from-pink-500 to-rose-500',
    badge: 'POPULAR',
    category: 'Profile Booster',
    addonType: 'profile_booster',
    requiresActivePlan: false
  },
  {
    name: 'Profile Booster - Premium',
    description: 'Ultimate visibility with exclusive features',
    credits: 250,
    creditsValue: 250,
    price: 999,
    features: [
      '250 sponsored impressions',
      'Always on top position',
      'Premium featured badge',
      'Complete analytics suite',
      'No subscription required',
      'Maximum visibility guarantee'
    ],
    icon: 'Crown',
    color: 'from-purple-500 to-indigo-500',
    badge: 'BEST VALUE',
    category: 'Profile Booster',
    addonType: 'profile_booster',
    requiresActivePlan: false
  },
  {
    name: 'Profile Booster - Enterprise',
    description: 'For serious seekers who want maximum exposure',
    credits: 1000,
    creditsValue: 1000,
    price: 2999,
    features: [
      '1000 sponsored impressions',
      'Guaranteed top position',
      'VIP featured badge',
      'Dedicated account manager',
      'No subscription required',
      'Best ROI for serious users'
    ],
    icon: 'Crown',
    color: 'from-orange-500 to-red-500',
    badge: 'ENTERPRISE',
    category: 'Profile Booster',
    addonType: 'profile_booster',
    requiresActivePlan: false
  },

  // LEGACY PLANS (backwards compatibility)
  {
    name: 'Super Likes Pack',
    description: 'Send unlimited super likes to show serious interest',
    credits: 30,
    price: 199,
    features: [
      '30 Super Likes',
      'Stand out from others',
      '3x higher response rate',
      'Priority notification to matches',
      'Valid for 30 days'
    ],
    icon: 'Heart',
    color: 'from-rose-500 to-pink-500',
    badge: '',
    category: 'Engagement'
  },
  {
    name: 'Message Boost',
    description: 'Get priority in message inbox and chat features',
    credits: 40,
    price: 249,
    features: [
      'Priority message delivery',
      'Read receipts enabled',
      'Typing indicators',
      'Message recall feature',
      'Chat analytics',
      'Valid for 40 days'
    ],
    icon: 'MessageCircle',
    color: 'from-green-500 to-emerald-500',
    badge: '',
    category: 'Communication'
  },
  {
    name: 'Profile Views Insights',
    description: 'See who viewed your profile with detailed analytics',
    credits: 20,
    price: 149,
    features: [
      'See all profile viewers',
      'Viewer demographics',
      'View timestamps',
      'Interest level indicators',
      '30 days access'
    ],
    icon: 'Eye',
    color: 'from-indigo-500 to-blue-500',
    badge: '',
    category: 'Analytics'
  },
  {
    name: 'Ultimate Package',
    description: 'All-in-one premium package with everything included',
    credits: 500,
    price: 1799,
    features: [
      '50x profile visibility',
      'Unlimited super likes',
      'Premium messaging features',
      'Complete analytics access',
      'Featured profile status',
      'VIP customer support',
      'Profile optimization tips',
      '500 days validity',
      'Monthly consultation call'
    ],
    icon: 'Sparkles',
    color: 'from-orange-500 to-red-500',
    badge: 'BEST DEAL',
    category: 'Premium Package'
  },
  {
    name: 'Rewind Pack',
    description: 'Go back and match with profiles you accidentally skipped',
    credits: 15,
    price: 99,
    features: [
      '15 Rewind actions',
      'Undo accidental swipes',
      'Second chance matching',
      'Valid for 15 days'
    ],
    icon: 'TrendingUp',
    color: 'from-yellow-500 to-orange-500',
    badge: '',
    category: 'Features'
  }
];

import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface Plan {
  _id?: string;
  name: string;
  description: string;
  credits: number;
  creditsValue?: number;  // For addon plans
  price: number;
  features: string[];
  icon: string;
  isActive: boolean;
  badge?: string;
  color: string;
  category?: string;
  addonType?: 'contact_views' | 'super_interests' | 'profile_booster' | 'video_calls' | 'interests' | 'other' | null;
  requiresActivePlan?: boolean;
}

// Feature templates for each addon type
const featureTemplates = {
  contact_views: [
    'Additional contact views',
    'Instantly added to your active plan',
    'Never expires while plan is active',
    'Perfect for when you run out',
    'View complete contact details',
    'Access to phone numbers',
    'Email addresses included',
    'WhatsApp contact details'
  ],
  super_interests: [
    'Super interest credits',
    'Stand out from regular interests',
    '5x higher response rate',
    'Added to your active plan',
    'Priority notification to matches',
    'Highlighted profile badge',
    'Premium interest indicator',
    'Better visibility'
  ],
  interests: [
    'Regular interest credits',
    'Send more connection requests',
    'Increase your match rate',
    'Added to your active plan',
    'Express interest in profiles',
    'Connect with more matches',
    'Daily interest limit increase',
    'More opportunities to connect'
  ],
  video_calls: [
    'Video call credits',
    'Connect face-to-face with matches',
    'Build trust before meeting',
    'Added to your active plan',
    'HD video quality',
    'Screen sharing option',
    'Call recording available',
    'Up to 60 minutes per call'
  ],
  profile_booster: [
    'Sponsored impressions',
    'Appear in top search results',
    'Show up first to potential matches',
    'No subscription required',
    '1 credit = 1 profile click',
    'Basic analytics dashboard',
    'Advanced analytics with insights',
    'Complete analytics suite',
    'Dedicated account manager',
    'Featured profile badge',
    'Premium featured badge',
    'VIP featured badge',
    'Maximum visibility guarantee',
    'Guaranteed top position',
    'Always on top position',
    'Priority in search results'
  ],
  other: [
    'Premium feature access',
    'Enhanced user experience',
    'Priority support',
    'Advanced capabilities',
    'Exclusive benefits',
    'Special privileges',
    'VIP treatment',
    'Custom features'
  ]
};

const iconOptions = [
  { value: 'Zap', label: 'Lightning (Zap)', icon: Zap },
  { value: 'Star', label: 'Star', icon: Star },
  { value: 'TrendingUp', label: 'Trending Up', icon: TrendingUp },
  { value: 'Heart', label: 'Heart', icon: Heart },
  { value: 'Crown', label: 'Crown', icon: Crown },
  { value: 'Sparkles', label: 'Sparkles', icon: Sparkles }
];

const colorOptions = [
  { value: 'from-pink-500 to-rose-500', label: 'Pink to Rose', preview: 'bg-gradient-to-r from-pink-500 to-rose-500' },
  { value: 'from-purple-500 to-indigo-500', label: 'Purple to Indigo', preview: 'bg-gradient-to-r from-purple-500 to-indigo-500' },
  { value: 'from-blue-500 to-cyan-500', label: 'Blue to Cyan', preview: 'bg-gradient-to-r from-blue-500 to-cyan-500' },
  { value: 'from-green-500 to-emerald-500', label: 'Green to Emerald', preview: 'bg-gradient-to-r from-green-500 to-emerald-500' },
  { value: 'from-orange-500 to-red-500', label: 'Orange to Red', preview: 'bg-gradient-to-r from-orange-500 to-red-500' },
  { value: 'from-amber-500 to-yellow-500', label: 'Amber to Yellow', preview: 'bg-gradient-to-r from-amber-500 to-yellow-500' }
];

export default function AdminPremiumPlans() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const dispatch = useAppDispatch();
  const { premiumPlans: premiumPlansState } = useAppSelector(state => state.admin);
  const { list: plans, loading } = premiumPlansState;

  const [showDialog, setShowDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [addonPlansEnabled, setAddonPlansEnabled] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState<string[]>(featureTemplates.other);

  const [formData, setFormData] = useState<Omit<Plan, '_id'>>({
    name: '',
    description: '',
    credits: 0,
    price: 0,
    features: [''],
    icon: 'Zap',
    isActive: true,
    badge: '',
    color: 'from-pink-500 to-rose-500',
    addonType: null,
    requiresActivePlan: false,
    creditsValue: 0
  });

  useEffect(() => {
    dispatch(fetchAdminPremiumPlans());
    fetchSettings();
  }, [dispatch]);

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddonPlansEnabled(data.settings?.features?.addonPlansEnabled || false);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Defensive check to ensure plans is always an array before rendering
  const safePlans = Array.isArray(plans) ? plans : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.description || (formData.credits || 0) <= 0 || (formData.price || 0) <= 0) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!Array.isArray(formData.features) || formData.features.filter(f => f && f.trim()).length === 0) {
      toast.error('Please add at least one feature');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const cleanedData = {
        ...formData,
        features: formData.features.filter(f => f && f.trim())
      };

      if (editingPlan?._id) {
        // Update existing plan
        await axios.put(
          `${API_URL}/admin/premium-plans/${editingPlan._id}`,
          cleanedData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Plan updated successfully');
      } else {
        // Create new plan
        await axios.post(
          `${API_URL}/admin/premium-plans`,
          cleanedData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Plan created successfully');
      }

      setShowDialog(false);
      setEditingPlan(null);
      resetForm();
      dispatch(fetchAdminPremiumPlans());
    } catch (error: any) {
      console.error('Error saving plan:', error);
      toast.error(error.response?.data?.message || 'Failed to save plan');
    }
  };

  const handleDelete = async (planId: string) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/premium-plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan deleted successfully');
      dispatch(fetchAdminPremiumPlans());
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Failed to delete plan');
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || '',
      description: plan.description || '',
      credits: plan.credits || 0,
      price: plan.price || 0,
      features: Array.isArray(plan.features) ? plan.features : [''],
      icon: plan.icon || 'Zap',
      isActive: plan.isActive ?? true,
      badge: plan.badge || '',
      color: plan.color || 'from-pink-500 to-rose-500',
      addonType: plan.addonType || null,
      requiresActivePlan: plan.requiresActivePlan || false,
      creditsValue: plan.creditsValue || 0
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      credits: 0,
      price: 0,
      features: [''],
      icon: 'Zap',
      isActive: true,
      badge: '',
      color: 'from-pink-500 to-rose-500',
      addonType: null,
      requiresActivePlan: false,
      creditsValue: 0
    });
  };

  const handleAddFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const handleRemoveFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const getIconComponent = (iconName: string) => {
    const option = iconOptions.find(opt => opt.value === iconName);
    return option?.icon || Zap;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin">
          <Crown className="size-8 text-pink-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Premium Plans Management</h2>
          <p className="text-gray-600 mt-1">Manage credit packages and pricing</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Addon Plans Toggle */}
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-2">
            <div className="text-right">
              <p className="text-sm font-semibold">Show Addon Plans</p>
              <p className="text-xs text-gray-500">Display on Plans tab</p>
            </div>
            <Switch
              checked={addonPlansEnabled}
              onCheckedChange={async (checked) => {
                setSettingsLoading(true);
                try {
                  const token = localStorage.getItem('token');
                  await axios.put(
                    `${API_URL}/settings/features`,
                    { addonPlansEnabled: checked },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  setAddonPlansEnabled(checked);
                  toast.success(`Addon plans ${checked ? 'enabled' : 'disabled'} successfully`);
                } catch (error) {
                  console.error('Error updating settings:', error);
                  toast.error('Failed to update settings');
                } finally {
                  setSettingsLoading(false);
                }
              }}
              disabled={settingsLoading}
            />
          </div>
          <Button
            onClick={() => {
              resetForm();
              setEditingPlan(null);
              setShowDialog(true);
            }}
            className="bg-gradient-to-r from-pink-600 to-purple-600 text-white"
          >
            <Plus className="size-4 mr-2" />
            Add New Plan
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Plans</p>
              <p className="text-2xl font-bold">{safePlans?.length || 0}</p>
            </div>
            <Crown className="size-8 text-pink-600" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Plans</p>
              <p className="text-2xl font-bold">{(safePlans || []).filter(p => p?.isActive).length}</p>
            </div>
            <CheckCircle className="size-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Credits</p>
              <p className="text-2xl font-bold">{safePlans.reduce((sum, p) => sum + p.credits, 0)}</p>
            </div>
            <Sparkles className="size-8 text-purple-600" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue Potential</p>
              <p className="text-2xl font-bold">₹{safePlans.reduce((sum, p) => sum + p.price, 0)}</p>
            </div>
            <DollarSign className="size-8 text-green-600" />
          </div>
        </Card>
      </div>

      {/* Example Plans Section */}
      {safePlans.length === 0 && (
        <div className="mb-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="size-6 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Example Plan Templates</h3>
                <p className="text-sm text-blue-700 mb-4">
                  Get inspired by these pre-designed plans. Click the copy icon to use any template as a starting point.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {examplePlans.slice(0, 6).map((example, idx) => {
              const IconComponent = getIconComponent(example.icon);
              return (
                <Card key={idx} className="relative overflow-hidden hover:shadow-lg transition-shadow">
                  <div className={`h-2 bg-gradient-to-r ${example.color}`} />
                  {example.badge && (
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-gradient-to-r from-pink-500 to-purple-600 text-white border-0 text-xs">
                        {example.badge}
                      </Badge>
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`inline-flex p-2 rounded-lg bg-gradient-to-br ${example.color}`}>
                        <IconComponent className="size-6 text-white" />
                      </div>
                      <Badge variant="outline" className="text-xs">{example.category}</Badge>
                    </div>
                    <h4 className="font-bold text-sm mb-1">{example.name}</h4>
                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">{example.description}</p>
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className="text-xl font-bold">₹{example.price}</span>
                      <span className="text-xs text-gray-500">/ {example.credits}cr</span>
                    </div>
                    <div className="space-y-1 mb-3">
                      {example.features.slice(0, 2).map((feature, fIdx) => (
                        <div key={fIdx} className="flex items-start gap-1">
                          <CheckCircle className="size-3 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-gray-700 line-clamp-1">{feature}</span>
                        </div>
                      ))}
                      {example.features.length > 2 && (
                        <p className="text-xs text-gray-500">+{example.features.length - 2} more</p>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        setFormData({
                          ...example,
                          isActive: true
                        });
                        setShowDialog(true);
                        toast.success('Template loaded! Modify and save.');
                      }}
                      size="sm"
                      variant="outline"
                      className="w-full"
                    >
                      <Copy className="size-3 mr-1" />
                      Use Template
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-4">
            <Button
              onClick={() => {
                const dialog = document.createElement('div');
                dialog.innerHTML = `
                  <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div class="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                      <div class="p-6 border-b sticky top-0 bg-white">
                        <div class="flex items-center justify-between">
                          <h2 class="text-2xl font-bold">All Example Plan Templates</h2>
                          <button onclick="this.closest('div').parentElement.remove()" class="p-2 hover:bg-gray-100 rounded-full">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                          </button>
                        </div>
                      </div>
                      <div class="p-6 grid grid-cols-1 md:grid-cols-3 gap-4" id="all-examples"></div>
                    </div>
                  </div>
                `;
                document.body.appendChild(dialog);

                // Populate the dialog with all example plans
                const examplesContainer = dialog.querySelector('#all-examples') as HTMLElement;
                examplePlans.forEach((example, idx) => {
                  const IconComponent = getIconComponent(example.icon);

                  // Create a mapping of icon names to SVG path data
                  const iconPathMap: Record<string, string> = {
                    'Star': 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
                    'Zap': 'M13 2L3 14h8l-1 8 10-12h-8l1-8z',
                    'Crown': 'M12 2l1.09 3.26L16.5 3.27 15 7l3.5 2-3.5 2 1.5 3.73-3.41-1.99L12 16l-1.09-3.26L7.5 14.73 9 11 5.5 9l3.5-2L7.5 3.27l3.41 1.99L12 2z',
                    'Heart': 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
                    'Gift': 'M20 12v10H4V12M2 7h20v5H2V7zm10 15V7m0 0H7.5a2.5 2.5 0 110-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 100-5C13 2 12 7 12 7z',
                    'TrendingUp': 'M23 6l-9.5 9.5-5-5L1 18M23 6h-7M23 6v7'
                  };

                  const iconPath = iconPathMap[example.icon] || iconPathMap['Zap']; // Default to Zap

                  const card = document.createElement('div');
                  card.className = 'relative overflow-hidden hover:shadow-lg transition-shadow';
                  card.innerHTML = `
                    <div class="h-2 bg-gradient-to-r ${example.color}"></div>
                    ${example.badge ? `
                      <div class="absolute top-4 right-4">
                        <span class="bg-gradient-to-r from-pink-500 to-purple-600 text-white border-0 text-xs badge">
                          ${example.badge}
                        </span>
                      </div>
                    ` : ''}
                    <div class="p-4">
                      <div class="flex items-start justify-between mb-3">
                        <div class="inline-flex p-2 rounded-lg bg-gradient-to-br ${example.color}">
                          <svg class="size-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${iconPath}"/>
                          </svg>
                        </div>
                        <span class="badge outline text-xs">${example.category}</span>
                      </div>
                      <h4 class="font-bold text-sm mb-1">${example.name}</h4>
                      <p class="text-xs text-gray-600 mb-2 line-clamp-2">${example.description}</p>
                      <div class="flex items-baseline gap-1 mb-3">
                        <span class="text-xl font-bold">₹${example.price}</span>
                        <span class="text-xs text-gray-500">/ ${example.credits}cr</span>
                      </div>
                      <div class="space-y-1 mb-3">
                        ${example.features.slice(0, 2).map((feature, fIdx) => `
                          <div key="${fIdx}" class="flex items-start gap-1">
                            <svg class="size-3 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                            </svg>
                            <span class="text-xs text-gray-700 line-clamp-1">${feature}</span>
                          </div>
                        `).join('')}
                        ${example.features.length > 2 ? `
                          <p class="text-xs text-gray-500">+${example.features.length - 2} more</p>
                        ` : ''}
                      </div>
                    </div>
                  `;

                  // Add click handler to use template
                  const useButton = document.createElement('button');
                  useButton.className = 'w-full mt-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1';
                  useButton.innerHTML = `
                    <svg class="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012 2h2a2 2 0 012-2"/>
                    </svg>
                    <span>Use Template</span>
                  `;
                  useButton.onclick = () => {
                    // Close dialog
                    dialog.remove();
                    // Set form data
                    setFormData({
                      ...example,
                      isActive: true
                    });
                    setShowDialog(true);
                    toast.success('Template loaded! Modify and save.');
                  };

                  card.querySelector('.p-4')?.appendChild(useButton);
                  examplesContainer.appendChild(card);
                });
              }}
              variant="ghost"
              size="sm"
            >
              View All {examplePlans.length} Example Templates →
            </Button>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {safePlans.map((plan) => {
            const IconComponent = getIconComponent(plan.icon);

            return (
              <Card key={plan._id} className="relative overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${plan.color}`} />

                {!plan.isActive && (
                  <div className="absolute top-4 right-4">
                    <Badge variant="secondary">Inactive</Badge>
                  </div>
                )}

                {plan.badge && plan.isActive && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-gradient-to-r from-pink-500 to-purple-600 text-white border-0">
                      {plan.badge}
                    </Badge>
                  </div>
                )}

                <div className="p-6">
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${plan.color} mb-4`}>
                    <IconComponent className="size-8 text-white" />
                  </div>

                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{plan.description}</p>

                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-3xl font-bold text-gray-900">₹{plan.price}</span>
                    <span className="text-gray-500 text-sm">/ {plan.credits} credits</span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="size-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                    {plan.features.length > 3 && (
                      <p className="text-xs text-gray-500">+{plan.features.length - 3} more features</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(plan)}
                      variant="outline"
                      className="flex-1"
                    >
                      <Edit className="size-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(plan._id!)}
                      variant="destructive"
                      size="icon"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {safePlans.length === 0 && (
        <div className="text-center py-16">
          <Crown className="size-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Plans Yet</h3>
          <p className="text-gray-600 mb-4">Create your first premium plan to get started</p>
          <Button
            onClick={() => {
              resetForm();
              setEditingPlan(null);
              setShowDialog(true);
            }}
            className="bg-gradient-to-r from-pink-600 to-purple-600 text-white"
          >
            <Plus className="size-4 mr-2" />
            Create Plan
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Edit Premium Plan' : 'Create New Premium Plan'}
            </DialogTitle>
            <DialogDescription>
              Configure the plan details, credits, and features
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Plan Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Profile Booster"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the plan"
                  rows={2}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="credits">Credits *</Label>
                  <Input
                    id="credits"
                    type="number"
                    min="1"
                    value={formData.credits}
                    onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) || 0 })}
                    placeholder="100"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="price">Price (₹) *</Label>
                  <Input
                    id="price"
                    type="number"
                    min="1"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                    placeholder="499"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="icon">Icon</Label>
                  <Select
                    value={formData.icon}
                    onValueChange={(value) => setFormData({ ...formData, icon: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map((option) => {
                        const Icon = option.icon;
                        return (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="size-4" />
                              {option.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="badge">Badge (Optional)</Label>
                  <Input
                    id="badge"
                    value={formData.badge || ''}
                    onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                    placeholder="e.g., POPULAR"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="color">Color Theme</Label>
                <Select
                  value={formData.color}
                  onValueChange={(value) => setFormData({ ...formData, color: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-4 rounded ${option.preview}`} />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Plan Type Dropdown */}
              <div>
                <Label htmlFor="planType">Plan Type / Addon Type</Label>
                <Select
                  value={formData.addonType || 'none'}
                  onValueChange={(value) => {
                    const newAddonType = value === 'none' ? null : value as any;
                    setFormData({
                      ...formData,
                      addonType: newAddonType
                    });

                    // Update available features based on plan type
                    if (newAddonType && featureTemplates[newAddonType]) {
                      setAvailableFeatures(featureTemplates[newAddonType]);
                    } else {
                      setAvailableFeatures(featureTemplates.other);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select plan type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Addon Type (Regular Plan)</SelectItem>
                    <SelectItem value="contact_views">
                      <div className="flex items-center gap-2">
                        <Eye className="size-4" />
                        Contact Views Addon
                      </div>
                    </SelectItem>
                    <SelectItem value="super_interests">
                      <div className="flex items-center gap-2">
                        <Star className="size-4" />
                        Super Interests Addon
                      </div>
                    </SelectItem>
                    <SelectItem value="interests">
                      <div className="flex items-center gap-2">
                        <Heart className="size-4" />
                        Regular Interests Addon
                      </div>
                    </SelectItem>
                    <SelectItem value="video_calls">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="size-4" />
                        Video Call Credits Addon
                      </div>
                    </SelectItem>
                    <SelectItem value="profile_booster">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="size-4" />
                        Profile Booster (Sponsored)
                      </div>
                    </SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Select addon type to auto-populate feature templates
                </p>
              </div>

              {/* Credits Value (for addons) */}
              {formData.addonType && (
                <div>
                  <Label htmlFor="creditsValue">Credits Value *</Label>
                  <Input
                    id="creditsValue"
                    type="number"
                    min="1"
                    value={formData.creditsValue || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      creditsValue: parseInt(e.target.value) || 0
                    })}
                    placeholder="e.g., 10"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How many credits this addon provides (e.g., 10 contact views, 50 super interests)
                  </p>
                </div>
              )}

              {/* Requires Active Plan Toggle */}
              {formData.addonType && formData.addonType !== 'profile_booster' && (
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <Label htmlFor="requiresActivePlan" className="font-semibold">
                      Requires Active Subscription
                    </Label>
                    <p className="text-sm text-gray-600">
                      User must have active plan to purchase this addon
                    </p>
                  </div>
                  <Switch
                    id="requiresActivePlan"
                    checked={formData.requiresActivePlan || false}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, requiresActivePlan: checked })
                    }
                  />
                </div>
              )}
            </div>

            {/* Features */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Features *</Label>
                <div className="flex gap-2">
                  {formData.addonType && (
                    <Select
                      onValueChange={(value) => {
                        if (!formData.features.includes(value)) {
                          setFormData({
                            ...formData,
                            features: [...formData.features, value]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Add from template" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFeatures.map((feature, idx) => (
                          <SelectItem key={idx} value={feature}>
                            {feature}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button
                    type="button"
                    onClick={handleAddFeature}
                    size="sm"
                    variant="outline"
                  >
                    <Plus className="size-4 mr-1" />
                    Add Custom
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      placeholder="e.g., 10x profile visibility boost"
                    />
                    {formData.features.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => handleRemoveFeature(index)}
                        variant="ghost"
                        size="icon"
                      >
                        <X className="size-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {formData.addonType
                  ? 'Select features from template dropdown or add custom features'
                  : 'Add custom features for this plan'}
              </p>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="isActive" className="font-semibold">Active Status</Label>
                <p className="text-sm text-gray-600">Make this plan available for purchase</p>
              </div>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  setEditingPlan(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-pink-600 to-purple-600 text-white"
              >
                <Save className="size-4 mr-2" />
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}